import React, { useMemo } from 'react';
import { Group, Radio, Stack, Text } from '@mantine/core';
import { descriptionForRole, sortTenantRoles } from './roleDescriptions';
import classes from './RolePicker.module.css';

type Props = {
    /** Assignable tenant role names (e.g. from listAssignableRoles). */
    roles: string[];
    value: string | null;
    onChange: (role: string) => void;
    label?: string;
    description?: string;
    error?: React.ReactNode;
    disabled?: boolean;
    name?: string;
};

/**
 * Single-choice role picker: each tenant role as a Radio.Card with plain-language
 * permission copy. Presentational — callers own form state and mutations.
 */
const RolePicker: React.FC<Props> = ({
    roles,
    value,
    onChange,
    label = 'Role',
    description,
    error,
    disabled = false,
    name = 'role',
}) => {
    const ordered = useMemo(() => sortTenantRoles(roles), [roles]);

    return (
        <Radio.Group
            name={name}
            label={label}
            description={description}
            error={error}
            value={value}
            onChange={role => {
                if (!disabled) onChange(role);
            }}
        >
            <Stack gap="xs" pt="sm">
                {ordered.map(role => (
                    <Radio.Card
                        key={role}
                        classNames={{ card: classes.root }}
                        value={role}
                        radius="md"
                        withBorder
                        p="md"
                        disabled={disabled}
                    >
                        <Group wrap="nowrap" align="flex-start" gap="sm">
                            <Radio.Indicator disabled={disabled} />
                            <div>
                                <Text className={classes.label}>{role}</Text>
                                <Text className={classes.description}>
                                    {descriptionForRole(role)}
                                </Text>
                            </div>
                        </Group>
                    </Radio.Card>
                ))}
            </Stack>
        </Radio.Group>
    );
};

export default RolePicker;
export type { Props as RolePickerProps };
